import { Unsubscribe } from 'router5/dist/types/base';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IRouter } from '../../infrastructure/router';
import { RouteParams, Routes } from '../../routes-table';
import { IErrorParsing } from '../../infrastructure/error-parsing';
import { PagingViewModel } from '../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { IGetUsersParams, IUserDetailsDTO, IUserFormDTO, IUsersDAO, IUsersListDTO } from '../../api/users.dao';
import { IModuleVM } from '../../infrastructure/components/module-vm';
import { IAlertsService } from '../../infrastructure/alerts';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../infrastructure/sub-windows';
import { MINIMAL_SEARCH_LENGTH } from '../../constants/common.constants';
import { COMMON_ITEMS_PER_PAGE } from '../../constants/pagination.contants';
import { IAccountsDAO } from '../../api/accounts.dao';
import { IRolesDAO } from '../../api/roles.dao';
import { UserForm } from './_user-form/user-form';
import TrackingServiceProvider from '../../tracking-service/tracking-service-provider';
import { TRACKING_EVENTS } from '../../tracking-service/tracking-values';

class UsersVM implements IModuleVM {
    @mobx.observable
    public usersList: IUserDetailsDTO[];
    @mobx.observable
    public isListLoading: boolean;
    @mobx.observable
    public isLoadingMore: boolean;
    @mobx.observable
    private userEmailToEditPopupMap: { [key: string]: ISubWindowFacade } = {};
    @mobx.observable
    public searchQuery: string = '';
    @mobx.observable
    public selectedFilterAccounts = '';
    @mobx.observable
    public selectedFilterRoles = '';
    @mobx.observable
    public shouldRedirectToFirstPage = false;
    @mobx.observable
    public paginationVM: PagingViewModel;
    private subscriptions: Unsubscribe[];
    @mobx.observable
    public oeStaffFilter: boolean = null;

    constructor(
        @IoC.Inject private readonly usersDAO: IUsersDAO = IoC.Container.get(IUsersDAO),
        @IoC.Inject private readonly accountsDAO: IAccountsDAO = IoC.Container.get(IAccountsDAO),
        @IoC.Inject private readonly rolesDAO: IRolesDAO = IoC.Container.get(IRolesDAO),
        @IoC.Inject private readonly router: IRouter<RouteParams> = IoC.Container.get(IRouter),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject
        private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider)
    ) {
        this.paginationVM = new PagingViewModel();
        mobx.reaction(this.paginationVM.getCurrentPage, this.fetchUsersList);
    }

    didMount(): void {
        this.subscriptions = [];

        this.subscriptions.push(this.router.listen(Routes.UsersList, async () => {
            this.fetchUsersList();
        }));
    }

    willUnmount(): void {
        this.subscriptions.forEach(x => x());
    }

    @mobx.action
    public fetchUsersList = async () => {
        this.isListLoading = true;
        if (this.shouldRedirectToFirstPage || !this.paginationVM.getCurrentPageForBE()) {
            this.paginationVM.selectPage(1);
            this.shouldRedirectToFirstPage = false;
        }
        try {
            const requestData: IGetUsersParams = {};
            if (this.searchQuery.trim()) requestData.query = this.searchQuery.trim();
            const response = await this.usersDAO.getUsers({
                page: this.paginationVM.getCurrentPageForBE(),
                size: this.paginationVM.getPageSize(),
                query: this.searchQuery ? this.searchQuery.trim() : '',
                accountIds: this.selectedFilterAccounts,
                roleIds: this.selectedFilterRoles,
                oeStaffOnly: this.oeStaffFilter
            });
            this.onFetchUsersListSuccess(response);
        } catch (error) {
            if (error.response.status !== 403) {
                this.onFetchUsersListError(error);
            }
        } finally {
            mobx.runInAction(() => {
                this.isListLoading = false;
            });
        }
    };

    @mobx.action
    private onFetchUsersListSuccess(response: IUsersListDTO): void {
        this.usersList = response.users;
        this.paginationVM.setTotalRecords(response.totalElements);
    }

    @mobx.action
    private onFetchUsersListError(error): void {
        this.alertsService.alert({
            message: this.errorParsingService.extractMessage(error)
        });
    }

    @mobx.action
    public resetSearchQuery = (): void => {
        this.searchQuery = '';
        this.shouldRedirectToFirstPage = true;
        this.fetchUsersList();
    };

    @mobx.action
    public searchUsers = (query: string): void => {
        this.searchQuery = query;
        if (query.trim().length >= MINIMAL_SEARCH_LENGTH) {
            this.shouldRedirectToFirstPage = true;
            this.fetchUsersList();
        }
    };

    public showUserForm = (params: IUserFormDTO) => {
        const { title, username } = params;
        return this.subWindowService.showSubWindow({
            componentToHost: UserForm,
            componentToHostProps: {
                fetchUsersList: this.fetchUsersList,
                closeEditUserForm: this.closeEditUserForm,
                username: username,
            },
            title: title,
        });
    };

    public isUserEditFormShown = (username: string) => {
        return this.userEmailToEditPopupMap[username]
            && this.userEmailToEditPopupMap[username].getStatus() !== SubWindowStatus.Closed;
    };

    public closeEditUserForm = (username: string) => {
        this.userEmailToEditPopupMap[username].close();
    };

    public editUser = async (username: string) => {
        let shallShowNewEditWindow = true;

        const alreadyShownSubWindow = this.userEmailToEditPopupMap[username];

        if (alreadyShownSubWindow && alreadyShownSubWindow.getStatus() !== SubWindowStatus.Closed) {
            alreadyShownSubWindow.maximize();
            shallShowNewEditWindow = false;
        }

        if (shallShowNewEditWindow) {
            mobx.runInAction(() => {
                this.userEmailToEditPopupMap[username] = this.showUserForm({title: 'Edit User', username: username});
            });
        }
    };

    public downloadUsersCSV = () => {
        this.usersDAO.getUsersCSV();
        this.trackingService.get().track(TRACKING_EVENTS.DOWNLOAD_ALL_USERS, {});
    };

    public fetchAccountsList = async (query: string): Promise<{ id: string; label: string }[]> => {
        const response = await this.accountsDAO.getAccounts({
            page: 0,
            size: COMMON_ITEMS_PER_PAGE,
            query: query?.trim() || ''
        });

        const initialValues = [{
            accountId: -1,
            name: 'All Accounts'
        }, {
            accountId: 0,
            name: 'None'
        }];

        return initialValues.concat(response.content).map(account => {
            return {
                id: account.accountId.toString(),
                label: account.name.toString(),
            };
        });
    };

    public fetchRolesList = async (): Promise<{ id: string; label: string }[]> => {
        const response = await this.rolesDAO.getAllRoles();

        const initialValues: { id: string; label: string }[] = [{
            id: 'superuser',
            label: 'Superuser'
        }, {
            id: 'none',
            label: 'None'
        }];

        return initialValues.concat(response.map(role => {
            return {
                id: role.name,
                label: role.name,
            };
        }));
    };

    @mobx.action
    public handleChangeAccountsFilter = (operatorsIds: string) => {
        this.selectedFilterAccounts = operatorsIds;
        this.shouldRedirectToFirstPage = true;
        this.fetchUsersList();
    };

    @mobx.action
    public onChangeOeStaffFilter = (value: boolean) => {
        this.paginationVM.selectPage(1);
        this.oeStaffFilter = value === this.oeStaffFilter ? null : value;
        this.fetchUsersList();
    };

    @mobx.action
    public handleChangeRolesFilter = (roleIds: string) => {
        this.selectedFilterRoles = roleIds;
        this.shouldRedirectToFirstPage = true;
        this.fetchUsersList();
    };
}

export { UsersVM };
